<template>
  <div v-if="showReconnectSection" class="zalo-reconnect-section">
    <div class="connection-status">
      <div class="status-header">
        <h3>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.CONNECTION_STATUS') }}</h3>
        <div class="status-indicator" :class="connectionStatusClass">
          {{ connectionStatusText }}
        </div>
      </div>
      
      <div v-if="!isConnected" class="reconnect-actions">
        <woot-button
          color-scheme="primary"
          :loading="isReconnecting"
          @click="startReconnection"
        >
          {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.RECONNECT') }}
        </woot-button>
      </div>
    </div>

    <!-- Reconnect Modal -->
    <woot-modal
      v-if="showReconnectModal"
      :show="showReconnectModal"
      :on-close="closeReconnectModal"
    >
      <div class="reconnect-modal">
        <div class="modal-header">
          <h2>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.RECONNECT_TITLE') }}</h2>
        </div>

        <div v-if="isLoading" class="text-center p-8">
          <woot-loading-state :message="statusMessage" />
        </div>

        <div v-else-if="qrCodeData" class="qr-section">
          <h3>{{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.SCAN_QR') }}</h3>
          <div class="qr-wrapper">
            <img :src="qrCodeData" alt="QR Code" class="qr-code" />
          </div>
          
          <div class="status-info">
            <div class="status-indicator" :class="qrStatusClass">
              {{ qrStatusText }}
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <woot-button
            color-scheme="secondary"
            @click="closeReconnectModal"
          >
            {{ $t('CONTACT_MGMT.EDIT.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import axios from 'axios';

export default {
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isReconnecting: false,
      showReconnectModal: false,
      qrCodeData: '',
      integrationId: '',
      accessToken: '',
      isLoading: false,
      statusMessage: '',
      qrStatus: 'waiting_qr_scan',
      qrStatusInterval: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    showReconnectSection() {
      return this.inbox.channel_type === 'Channel::ZaloPersonal';
    },
    sessionData() {
      return this.getSessionData();
    },
    isConnected() {
      return this.sessionData.integration_id && this.sessionData.is_healthy === true;
    },
    connectionStatusClass() {
      return this.isConnected ? 'status-connected' : 'status-disconnected';
    },
    connectionStatusText() {
      return this.isConnected 
        ? this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.CONNECTED')
        : this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.STATUS.DISCONNECTED');
    },
    qrStatusClass() {
      const statusMap = {
        waiting_qr_scan: 'status-waiting',
        qr_scanned: 'status-scanned',
        logged_in: 'status-success',
        login_failed: 'status-failed',
      };
      return statusMap[this.qrStatus] || 'status-waiting';
    },
    qrStatusText() {
      const statusMap = {
        waiting_qr_scan: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.WAITING'),
        qr_scanned: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.SCANNED'),
        logged_in: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.SUCCESS'),
        login_failed: this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_STATUS.FAILED'),
      };
      return statusMap[this.qrStatus] || '';
    },
    microserviceUrl() {
      return window.hichatConfig?.zaloPersonalMicroserviceUrl || 'http://localhost:8000';
    },
  },
  beforeUnmount() {
    if (this.qrStatusInterval) {
      clearInterval(this.qrStatusInterval);
    }
  },
  methods: {
    getSessionData() {
      try {
        return JSON.parse(this.inbox.session_data || '{}');
      } catch (error) {
        return {};
      }
    },

    showAlert(message) {
      useAlert(message);
    },
    
    async startReconnection() {
      try {
        this.isReconnecting = true;
        this.isLoading = true;
        this.statusMessage = 'Đang khởi tạo kết nối lại...';
        this.showReconnectModal = true;

        const response = await axios.post(`${this.microserviceUrl}/api/register`, {
          hichat_account_id: this.currentAccountId.toString(),
          hichat_inbox_id: this.inbox.id.toString(),
          hichat_api_key: this.currentUser.access_token,
          hichat_base_url: window.location.origin,
          agent_id: this.currentUser.id.toString(),
        });

        if (response.data.success) {
          this.qrCodeData = response.data.qr_code;
          this.integrationId = response.data.integration_id;
          this.accessToken = response.data.access_token;
          this.statusMessage = response.data.message || 'Vui lòng quét mã QR';
          this.startQRStatusCheck();
        } else {
          throw new Error(response.data.message || 'Đăng ký thất bại');
        }
      } catch (error) {
        this.showAlert(error.message || 'Có lỗi xảy ra khi khởi tạo kết nối lại');
        this.closeReconnectModal();
      } finally {
        this.isReconnecting = false;
        this.isLoading = false;
      }
    },

    startQRStatusCheck() {
      this.qrStatusInterval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${this.microserviceUrl}/api/qr-status/${this.integrationId}`
          );
          
          if (response.data.success) {
            // Nếu response trả về integration_id thì cập nhật lại
            if (response.data.integration_id) {
              this.integrationId = response.data.integration_id;
            }
            this.qrStatus = response.data.status;
            
            if (response.data.status === 'logged_in' || response.data.is_connected) {
              clearInterval(this.qrStatusInterval);
              if (response.data.access_token) {
                this.accessToken = response.data.access_token;
              }
              await this.updateConnection();
            } else if (response.data.status === 'new_qr' && response.data.qr_code) {
              this.qrCodeData = response.data.qr_code;
            } else if (response.data.status === 'login_failed') {
              clearInterval(this.qrStatusInterval);
              this.showAlert('Đăng nhập thất bại. Vui lòng thử lại.');
              this.closeReconnectModal();
            }
          }
        } catch (error) {
          // Continue checking
        }
      }, 2000);
    },

    async updateConnection() {
      try {
        const sessionData = {
          integration_id: this.integrationId,
          access_token: this.accessToken,
          is_healthy: true,
          last_health_check: new Date().toISOString(),
        };

        const currentSessionData = this.getSessionData();
        const mergedSessionData = {
          ...currentSessionData,
          ...sessionData,
          reconnected_at: new Date().toISOString()
        };

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            session_data: JSON.stringify(mergedSessionData)
          }
        });

        await axios.post(
          `${this.microserviceUrl}/api/integration/${this.integrationId}/update`,
          { hichat_inbox_id: this.inbox.id.toString() },
          { headers: { 'X-Access-Token': this.accessToken } }
        );

        await this.$store.dispatch('inboxes/get');

        this.showAlert('Kết nối lại thành công!');
        this.closeReconnectModal();
        this.$emit('connection-updated');
      } catch (error) {
        console.error('Reconnect error:', error);
        this.showAlert(error.message || 'Có lỗi khi cập nhật kết nối');
      }
    },

    closeReconnectModal() {
      this.showReconnectModal = false;
      this.qrCodeData = '';
      this.integrationId = '';
      this.accessToken = '';
      this.qrStatus = 'waiting_qr_scan';
      if (this.qrStatusInterval) {
        clearInterval(this.qrStatusInterval);
      }
    },
  },
};
</script>

<style scoped>
.zalo-reconnect-section {
  margin: 1.5rem 0;
  padding: 1.5rem;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  background: var(--color-background);
}

.connection-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-body);
}

.status-indicator {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-connected {
  background-color: var(--g-100);
  color: var(--g-700);
}

.status-disconnected {
  background-color: var(--r-100);
  color: var(--r-700);
}

.reconnect-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-header {
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-body);
}

.qr-section {
  text-align: center;
  margin-bottom: 1.5rem;
}

.qr-wrapper {
  margin: 1rem 0;
  display: inline-block;
  padding: 1rem;
  background: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--color-shadow);
}

.qr-code {
  max-width: 250px;
  height: auto;
  border-radius: 4px;
}

.status-info {
  margin: 1rem 0;
}

.status-waiting {
  background-color: var(--b-100);
  color: var(--b-700);
}

.status-scanned {
  background-color: var(--y-100);
  color: var(--y-700);
}

.status-success {
  background-color: var(--g-100);
  color: var(--g-700);
}

.status-failed {
  background-color: var(--r-100);
  color: var(--r-700);
}

.modal-actions {
  text-align: right;
  margin-top: 1.5rem;
}
</style> 